import { Menu, Search } from 'lucide-react';
import { Logo } from './Logo';
import { UserMenu } from './UserMenu';
import { NewMenu } from './NewMenu';
import { WorkspaceBadge } from './WorkspaceBadge';
import { TrialBadge } from '@/components/billing/TrialBadge';
import { useMobileNavStore } from '@/store/useMobileNavStore';
import { useCommandPaletteStore } from '@/store/useCommandPaletteStore';

// ⌘ on Apple platforms, Ctrl elsewhere — resolved once at module load.
const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.userAgent);

/**
 * Global header. Only real controls live here now:
 *   - Logo (home link)
 *   - Workspace badge (workspace switcher)
 *   - Trial countdown badge (during trial only)
 *   - User menu
 *
 * The bell, help icon, and search bar were placeholder shells from an
 * early phase with no wired behavior. Removed in Phase 30.35.2 because:
 *   1. The bell showed a fake "unread" red dot that never meant anything
 *   2. The help icon had no onClick and nothing to open
 *   3. The search bar had no onChange/onSubmit and never queried anything
 * Project-level insights already have a real bell inside ProjectDetailPage
 * (Phase 30.35). Global search / help / notifications will be added as
 * their own phases when they have real behavior.
 */
export function Header() {
  const openNav = useMobileNavStore((s) => s.open);
  const openPalette = useCommandPaletteStore((s) => s.open);
  return (
    <header className="bg-white border-b border-line shadow-xs sticky top-0 z-[100] min-h-[64px] pt-safe-top">
      <div className="flex items-center justify-between px-4 lg:px-6 py-3 gap-3 lg:gap-4">
        <div className="flex items-center gap-2 lg:gap-3">
          {/* Hamburger — phones only; opens the navigation drawer */}
          <button
            type="button"
            onClick={openNav}
            aria-label="Open menu"
            className="lg:hidden p-2 -ml-2 text-ink-secondary hover:text-ink-primary"
          >
            <Menu className="w-5 h-5" />
          </button>
          <Logo />
          <WorkspaceBadge />
        </div>

        {/* Command palette trigger — fills the previously-empty center region */}
        <div className="flex-1 flex justify-end lg:justify-start min-w-0">
          <button
            type="button"
            onClick={openPalette}
            aria-label="Search (open command palette)"
            className="flex items-center gap-2 rounded-md border border-line bg-surface-muted/60 px-2.5 lg:px-3 py-1.5 text-sm text-ink-tertiary hover:bg-surface-muted hover:text-ink-secondary transition-colors w-auto lg:w-full lg:max-w-sm"
          >
            <Search className="w-4 h-4 shrink-0" />
            <span className="hidden lg:inline flex-1 text-left">Search…</span>
            <kbd className="hidden lg:inline text-[10px] font-mono border border-line rounded px-1.5 py-0.5">
              {IS_MAC ? '⌘K' : 'Ctrl K'}
            </kbd>
          </button>
        </div>

        <div className="flex items-center gap-2">
          {/* Global "+ New" — start any top-level object from anywhere */}
          <NewMenu />
          {/* Phase 23: trial countdown — only renders during trial */}
          <TrialBadge className="mr-1" />
          <UserMenu />
        </div>
      </div>
    </header>
  );
}
